import axios from 'axios';
import { clearAuthCookies } from '../context/AuthContext';

// Using React dev server proxy, so no need for full URL
const api = axios.create({
  timeout: 30000, // 10 second timeout
  withCredentials: true, // This allows cookies to be sent with requests
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  },
});

// Add response interceptor to handle 401 responses
api.interceptors.response.use(
  (response) => {
    // Return successful responses as-is
    return response;
  },
  (error) => {
    // Handle timeout errors
    if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
      const timeoutError = new Error('Request timed out. Please check your internet connection and try again.');
      timeoutError.isTimeout = true;
      return Promise.reject(timeoutError);
    }

    // Handle 401 Unauthorized responses
    if (error.response && error.response.status === 401) {
      console.log('401 Unauthorized - clearing auth cookies and redirecting');
      // Clear only authentication cookies (preserve tour/onboarding preferences)
      clearAuthCookies();
      // Redirect to login (reload page to trigger auth flow)
      window.location.reload();
      // Return a rejected promise to prevent further processing
      return Promise.reject(new Error('Session expired - redirecting to login'));
    }

    // For all other errors, pass them through unchanged
    return Promise.reject(error);
  }
);

export const clientService = {
  getClientData: async () => {
    try {
      const response = await api.get('/clientCommonData.fxl', {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      // Check if the API response indicates an error (even with 200 status)
      if (response.data?.status === 'error') {
        const error = new Error(response.data.message || 'API returned error status');
        error.response = {
          data: response.data,
          status: response.status,
          statusText: response.statusText
        };
        throw error;
      }

      return response.data;
    } catch (error) {
      console.error('Client data API error:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        headers: error.response?.headers,
        data: error.response?.data
      });
      throw error;
    }
  }
};

export const recommendationsService = {
  getRecommendationsPageData: async (authCode, paginationParams = {}) => {
    const params = {
      limit: paginationParams.pageSize || 10,
      page: paginationParams.pageIndex || 0,
      user: paginationParams.user || '',
      country: paginationParams.country || '',
      profile: paginationParams.profile || '',
      fromDate: paginationParams.fromDate || '',
      toDate: paginationParams.toDate || '',
      labels: paginationParams.labels || '',
      recommendationId: paginationParams.recommendationId || '',
      test: paginationParams.test || 0
    };

    try {
      const response = await api.get('/recommendationsPageData.fxl', {
        params,
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });


      // Check if the API response indicates an error (even with 200 status)
      if (response.data?.status === 'error') {
        const error = new Error(response.data.message || 'API returned error status');
        error.response = {
          data: response.data,
          status: response.status,
          statusText: response.statusText
        };
        throw error;
      }

      // Extract recommendations array from response
      const recommendations = response.data?.recommendations || response.data || [];
      const totalCount = response.data?.totalCount || response.data?.total || recommendations.length;
      const countries = response.data?.countries || [];
      const labels = response.data?.labels || [];


      // Pass through all fields from the original response to preserve metadata
      return {
        ...response.data, // Include all original fields
        recommendations: Array.isArray(recommendations) ? recommendations : [recommendations],
        totalCount,
        pageCount: Math.ceil(totalCount / params.limit),
        countries,
        labels
      };
    } catch (error) {
      console.error('API Error:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        headers: error.response?.headers,
        data: error.response?.data
      });
      throw error;
    }
  }
};

export const trendsService = {
  getTrendsData: async (fromDate, toDate, countries = [], profile = '') => {
    const params = {
      fromDate,
      toDate
    };

    // Add countries parameter if provided
    if (countries.length > 0) {
      params.countries = countries.map(c => c.id).join(',');
    }

    // Add profile parameter if provided (admin client filter)
    if (profile) {
      params.profile = profile;
    }

    try {
      const response = await api.get('/trends.fxl', {
        params,
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      // Check if the API response indicates an error (even with 200 status)
      if (response.data?.status === 'error') {
        const error = new Error(response.data.message || 'API returned error status');
        error.response = {
          data: response.data,
          status: response.status,
          statusText: response.statusText
        };
        throw error;
      }

      if (response.data?.status !== 'success') {
        throw new Error(response.data?.message || 'Failed to fetch trends data');
      }

      return response.data;
    } catch (error) {
      console.error('Trends API error:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        headers: error.response?.headers,
        data: error.response?.data
      });
      throw error;
    }
  }
};

export const productUsageService = {
  getProductUsageData: async (paginationParams = {}) => {
    const params = {
      fromDate: paginationParams.fromDate || '',
      toDate: paginationParams.toDate || '',
      country: paginationParams.country || '',
      limit: paginationParams.pageSize || 25,
      page: paginationParams.pageIndex || 0,
      search: paginationParams.search || '',
      profile: paginationParams.profile || ''
    };

    try {
      const response = await api.get('/productUsageData.fxl', {
        params,
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      // Check if the API response indicates an error (even with 200 status)
      if (response.data?.status === 'error') {
        const error = new Error(response.data.message || 'API returned error status');
        error.response = {
          data: response.data,
          status: response.status,
          statusText: response.statusText
        };
        throw error;
      }

      if (response.data?.status !== 'success') {
        throw new Error(response.data?.message || 'Failed to fetch product usage data');
      }

      // Extract data from response
      const products = response.data?.products || [];
      const totalCount = response.data?.totalCount || products.length;

      return {
        ...response.data,
        products,
        totalCount,
        pageCount: Math.ceil(totalCount / params.limit)
      };
    } catch (error) {
      console.error('Product usage API error:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        headers: error.response?.headers,
        data: error.response?.data
      });
      throw error;
    }
  },

  // Returns download URL for export (format: 'csv' or 'xlsx')
  getDownloadUrl: (params = {}) => {
    const urlParams = new URLSearchParams();
    urlParams.append('fromDate', params.fromDate || '');
    urlParams.append('toDate', params.toDate || '');
    urlParams.append('country', params.country || '');
    urlParams.append('format', params.format || 'csv');
    urlParams.append('search', params.search || '');
    urlParams.append('profile', params.profile || '');
    return `/downloadProductUsageData.fxl?${urlParams.toString()}`;
  }
};

export const contactRequestsService = {
  getContactRequestsData: async (paginationParams = {}) => {
    const params = {
      limit: paginationParams.pageSize || 25,
      page: paginationParams.pageIndex || 0,
      fromDate: paginationParams.fromDate || '',
      toDate: paginationParams.toDate || '',
      country: paginationParams.country || '',
      profile: paginationParams.profile || '',
      user: paginationParams.user || '',
      phone: paginationParams.phone || '',
      postCode: paginationParams.postCode || '',
      recommendationId: paginationParams.recommendationId || '',
      comments: paginationParams.comments || ''
    };

    try {
      const response = await api.get('/contactRequestsPageData.fxl', {
        params,
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      // Check if the API response indicates an error (even with 200 status)
      if (response.data?.status === 'error') {
        const error = new Error(response.data.message || 'API returned error status');
        error.response = {
          data: response.data,
          status: response.status,
          statusText: response.statusText
        };
        throw error;
      }

      // Extract data from response
      const requests = response.data?.requests || [];
      const totalCount = response.data?.totalCount || requests.length;

      return {
        ...response.data,
        requests,
        totalCount,
        pageCount: Math.ceil(totalCount / params.limit)
      };
    } catch (error) {
      console.error('Contact requests API error:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        headers: error.response?.headers,
        data: error.response?.data
      });
      throw error;
    }
  },

  // Returns download URL for export (format: 'csv' or 'xlsx')
  getDownloadUrl: (params = {}) => {
    const urlParams = new URLSearchParams();
    urlParams.append('fromDate', params.fromDate || '');
    urlParams.append('toDate', params.toDate || '');
    urlParams.append('country', params.country || '');
    urlParams.append('profile', params.profile || '');
    urlParams.append('user', params.user || '');
    urlParams.append('phone', params.phone || '');
    urlParams.append('postCode', params.postCode || '');
    urlParams.append('recommendationId', params.recommendationId || '');
    urlParams.append('comments', params.comments || '');
    urlParams.append('format', params.format || 'csv');
    return `/downloadContactRequestsData.fxl?${urlParams.toString()}`;
  }
};

export const authService = {
  resetPassword: async (email) => {
    try {
      // Create a separate axios instance for Auth0 API calls (different base URL)
      const auth0Api = axios.create({
        timeout: 30000, // 10 second timeout
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      const response = await auth0Api.post(
        `https://${process.env.REACT_APP_AUTH0_DOMAIN}/dbconnections/change_password`,
        {
          client_id: process.env.REACT_APP_AUTH0_CLIENT_ID,
          email: email,
          connection: 'Username-Password-Authentication'
        }
      );

      return response.data;
    } catch (error) {
      console.error('Password reset API error:', error);

      // Handle timeout specifically
      if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
        const timeoutError = new Error('Password reset request timed out. Please check your internet connection and try again.');
        timeoutError.isTimeout = true;
        throw timeoutError;
      }

      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        headers: error.response?.headers,
        data: error.response?.data
      });
      throw error;
    }
  }
};

export default api;
